// `services/multi-select-modal/multi-select-modal.service.ng1.ts` + `multi-select-modal.tpl.html`,
// ported — "Select candidates": All in page / All results.
//
// Upstream opens it with `modalService.show({ closeAfterOk: false, bodyTemplateUrl }, {
// useUnifiedTemplate: true, windowClass: 'dialog-unified shortlist-dialogue multi-select-modal' })`,
// so it renders through `UnifiedModal`, and `_multi-select-modal.scss` hangs the 200px width and the
// #f9feff background off `.multi-select-modal .modal-dialog` / `.modal-content`.
//
// The header reads "Select candidates" in both products; the template has no product switch.
import { useRef, useState, type ReactElement } from 'react'
import { UnifiedModal } from '../modal/UnifiedModal.tsx'
import { multiSelectHasSelection } from './shortlistModals.ts'

export interface MultiSelectModalProps {
  /** `params.onSelectAllInPageClicked`. */
  onSelectAllInPage: () => Promise<void> | void
  /** `params.onSelectAllInResultsClicked`. */
  onSelectAllInResults: () => Promise<void> | void
  /** `params.getCheckedCandidateIds().length` — read on open and again after each option. */
  getCheckedCount: () => number
  /**
   * `modalInstance.result`: `true` when an option left at least one candidate checked
   * (`$close(true)`), `false` for the X, Escape or a backdrop click (the dismissal).
   */
  onClose: (confirmed: boolean) => void
}

type Option = 'page' | 'results'

export function MultiSelectModal({
  onSelectAllInPage,
  onSelectAllInResults,
  getCheckedCount,
  onClose,
}: MultiSelectModalProps): ReactElement {
  // `ctrl.checkedCandidateIds` is captured in `init`, so the count the form validates is the one
  // at open until an option refreshes it.
  const [checkedCount, setCheckedCount] = useState(getCheckedCount)
  // `ctrl.form.$submitted` — the error line shows only after `ok()` has run.
  const [submitted, setSubmitted] = useState(false)
  // `click-and-disable` on each `<a>`: `disabled="disabled"` while its handler runs, and a click
  // on a disabled one does nothing. The ref is the guard; the state is the attribute.
  const pendingRef = useRef<Set<Option>>(new Set())
  const [pending, setPending] = useState<ReadonlySet<Option>>(new Set())

  const choose = async (option: Option, run: () => Promise<void> | void): Promise<void> => {
    if (pendingRef.current.has(option)) return
    pendingRef.current.add(option)
    setPending(new Set(pendingRef.current))
    try {
      await run()
      // `postOptionClicked`: refresh the count, then `ok()` — which upstream defers a tick with
      // `$timeout` only so the hidden input's model is digested before the form validates.
      const count = getCheckedCount()
      setCheckedCount(count)
      setSubmitted(true)
      if (multiSelectHasSelection(count)) onClose(true)
    } finally {
      pendingRef.current.delete(option)
      setPending(new Set(pendingRef.current))
    }
  }

  // An `<a>` has no `disabled` property, so the directive writes the attribute instead.
  const disabledAttr = (option: Option): Record<string, string> =>
    pending.has(option) ? { disabled: 'disabled' } : {}

  const cancel = (): void => {
    onClose(false)
  }

  return (
    <UnifiedModal open windowClass="dialog-unified shortlist-dialogue multi-select-modal" onClose={cancel}>
      <div className="multi-select-modal-container">
        <div className="shortlist-dialogue-x-icon">
          <i className="far fa-times" onClick={cancel} />
        </div>
        <div className="multi-select-modal-header-modal">
          <span>Select candidates</span>
        </div>
        <div className="multi-select-modal-body">
          {/* `ng-form="ctrl.form"` — a div, not a `<form>`; the validity is `multiSelectHasSelection`. */}
          <div className="multi-select-modal-form">
            <input
              name="checkedCandidateIds"
              type="number"
              value={checkedCount}
              min={1}
              readOnly
              className="checked-candidate-ids-input"
            />
            <ul className="multi-select-options">
              <li className="multi-select-option">
                <a
                  href=""
                  {...disabledAttr('page')}
                  className="multi-select-option-btn"
                  onClick={(event) => {
                    event.preventDefault()
                    void choose('page', onSelectAllInPage)
                  }}
                >
                  All in page
                </a>
              </li>
              <li className="multi-select-option">
                <a
                  href=""
                  {...disabledAttr('results')}
                  className="multi-select-option-btn"
                  onClick={(event) => {
                    event.preventDefault()
                    void choose('results', onSelectAllInResults)
                  }}
                >
                  All results
                </a>
              </li>
            </ul>
            {submitted && !multiSelectHasSelection(checkedCount) ? (
              <label className="has-error">No candidates were selected</label>
            ) : null}
          </div>
        </div>
      </div>
    </UnifiedModal>
  )
}
